import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Spin,
  Alert,
  Table,
  Tag,
  Input,
  InputNumber,
  Select,
  Button,
  message,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import customClientAxios from "../../customAxios";
import {
  PlatformAnalyticsUsersForIgUpdate,
  PlatformAnalyticsUpdateUserIgDetails,
} from "../../utils/ApiEndpoints";
import { EVENT_HOST_TYPES } from "../../utils/eventHostTypes";
import { PageContainer } from "../paUtils";
import styled from "styled-components";

const TableCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  border: 1px solid #e2e8f0;
  overflow: hidden;
`;

const TableHeader = styled.div`
  padding: 18px 24px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #f1f5f9;
`;

const TableTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
`;

const TotalBadge = styled.span`
  font-size: 12px;
  color: #64748b;
  background: #f1f5f9;
  padding: 2px 10px;
  border-radius: 20px;
`;

const ConnectedTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  background: ${(p) => (p.$connected ? "rgba(34,197,94,0.12)" : "#f1f5f9")};
  color: ${(p) => (p.$connected ? "#16a34a" : "#94a3b8")};
`;

const fmtDate = (v) =>
  v
    ? new Date(v).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

// Compare current row state vs the saved baseline.
const isRowDirty = (current, saved) => {
  if (!current || !saved) return false;
  const norm = (v) => (v === undefined || v === "" ? null : v);
  return (
    norm(current.instagramProfileUrl) !== norm(saved.instagramProfileUrl) ||
    norm(current.instagramFollowerCount) !==
      norm(saved.instagramFollowerCount) ||
    norm(current.eventHostType) !== norm(saved.eventHostType)
  );
};

function UpdateInstagramDetailsTab() {
  const [data, setData] = useState({ users: [], pagination: {} });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Per-row editable state, keyed by userId.
  // Each entry: { current: {url, count, type}, saved: {url, count, type} }
  const [rowState, setRowState] = useState({});
  const [savingId, setSavingId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, limit: 20 };
      if (search.trim()) params.search = search.trim();
      const res = await customClientAxios().get(
        PlatformAnalyticsUsersForIgUpdate,
        { params }
      );
      const payload = res.data.data;
      setData(payload);
      // Seed row state from server values
      const seed = {};
      (payload.users || []).forEach((u) => {
        const initial = {
          instagramProfileUrl: u.instagramProfileUrl ?? null,
          instagramFollowerCount: u.instagramFollowerCount ?? null,
          eventHostType: u.eventHostType ?? null,
        };
        seed[u.userId] = { current: { ...initial }, saved: { ...initial } };
      });
      setRowState(seed);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  useEffect(() => {
    setPage(1);
  }, [search]);

  const updateField = (userId, field, value) => {
    setRowState((prev) => {
      const row = prev[userId];
      if (!row) return prev;
      return {
        ...prev,
        [userId]: {
          ...row,
          current: { ...row.current, [field]: value },
        },
      };
    });
  };

  const handleSave = async (userId) => {
    const row = rowState[userId];
    if (!row) return;
    setSavingId(userId);
    try {
      const body = {
        instagramProfileUrl: row.current.instagramProfileUrl,
        instagramFollowerCount: row.current.instagramFollowerCount,
        eventHostType: row.current.eventHostType,
      };
      await customClientAxios().patch(
        PlatformAnalyticsUpdateUserIgDetails(userId),
        body
      );
      // Bake current into saved => row becomes pristine, Save button disables
      setRowState((prev) => ({
        ...prev,
        [userId]: {
          current: { ...prev[userId].current },
          saved: { ...prev[userId].current },
        },
      }));
      message.success("Instagram details updated");
    } catch (e) {
      message.error(
        e?.response?.data?.error || "Failed to update Instagram details"
      );
    } finally {
      setSavingId(null);
    }
  };

  const hostTypeOptions = useMemo(
    () => EVENT_HOST_TYPES.map((t) => ({ label: t, value: t })),
    []
  );

  const columns = [
    {
      title: "User",
      key: "user",
      width: 220,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600, color: "#1e293b", fontSize: 13 }}>
            {r.Name || "—"}
          </div>
          <div style={{ fontSize: 11, color: "#94a3b8" }}>
            {r.userEmail || "—"}
          </div>
          {r.userNumber && (
            <div style={{ fontSize: 11, color: "#94a3b8" }}>{r.userNumber}</div>
          )}
        </div>
      ),
    },
    {
      title: "Signup Date",
      dataIndex: "createdDate",
      key: "createdDate",
      width: 120,
      render: fmtDate,
    },
    {
      title: "IG Connected",
      key: "igConnected",
      width: 160,
      render: (_, r) =>
        r.igConnected ? (
          <ConnectedTag $connected>
            ✓ {r.igUsername || "@connected"}
          </ConnectedTag>
        ) : (
          <ConnectedTag>—</ConnectedTag>
        ),
    },
    {
      title: "Profile URL",
      key: "instagramProfileUrl",
      width: 260,
      render: (_, r) => {
        const row = rowState[r.userId];
        return (
          <Input
            placeholder="https://instagram.com/..."
            value={row?.current?.instagramProfileUrl ?? ""}
            onChange={(e) =>
              updateField(
                r.userId,
                "instagramProfileUrl",
                e.target.value || null
              )
            }
            size="small"
          />
        );
      },
    },
    {
      title: "Follower Count",
      key: "instagramFollowerCount",
      width: 150,
      render: (_, r) => {
        const row = rowState[r.userId];
        return (
          <InputNumber
            placeholder="0"
            min={0}
            value={row?.current?.instagramFollowerCount ?? null}
            onChange={(v) =>
              updateField(
                r.userId,
                "instagramFollowerCount",
                v === undefined ? null : v
              )
            }
            size="small"
            style={{ width: "100%" }}
          />
        );
      },
    },
    {
      title: "Event Host Type",
      key: "eventHostType",
      width: 180,
      render: (_, r) => {
        const row = rowState[r.userId];
        return (
          <Select
            placeholder="Select type"
            value={row?.current?.eventHostType ?? undefined}
            onChange={(v) => updateField(r.userId, "eventHostType", v ?? null)}
            options={hostTypeOptions}
            size="small"
            style={{ width: "100%" }}
            allowClear
          />
        );
      },
    },
    {
      title: "Save",
      key: "save",
      width: 90,
      align: "center",
      render: (_, r) => {
        const row = rowState[r.userId];
        const dirty = row ? isRowDirty(row.current, row.saved) : false;
        return (
          <Button
            type="primary"
            size="small"
            disabled={!dirty}
            loading={savingId === r.userId}
            onClick={() => handleSave(r.userId)}
          >
            Save
          </Button>
        );
      },
    },
  ];

  const pagination = data.pagination || {};

  return (
    <PageContainer>
      {error && (
        <Alert
          type="error"
          message={error}
          style={{ marginBottom: 20 }}
          closable
          onClose={() => setError(null)}
        />
      )}

      <TableCard>
        <TableHeader>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <TableTitle>Update Instagram Details</TableTitle>
            <TotalBadge>{pagination.total ?? 0} users</TotalBadge>
          </div>
          <Input
            placeholder="Search by name, email or mobile..."
            prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 280 }}
            allowClear
          />
        </TableHeader>
        <Spin spinning={loading}>
          <Table
            dataSource={data.users || []}
            columns={columns}
            rowKey="userId"
            size="middle"
            pagination={{
              current: page,
              pageSize: 20,
              total: pagination.total || 0,
              onChange: setPage,
              showSizeChanger: false,
              showTotal: (t) => `${t} users`,
            }}
            scroll={{ x: 1180 }}
          />
        </Spin>
      </TableCard>
    </PageContainer>
  );
}

export default UpdateInstagramDetailsTab;
